import React, { useMemo, useState } from "react";
import { displayValue } from "../../utils/displayValue";

import "../../styles/hrPageShell.css";
function normalizeText(value) {
  if (value === undefined || value === null) return "";
  if (typeof value === "object") {
    return displayValue(value);
  }
  return String(value).trim();
}

function formatMoney(value, currency = "") {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return displayValue(value);
  try {
    return new Intl.NumberFormat("en-IN", {
      style: currency ? "currency" : "decimal",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    }).format(number);
  } catch {
    return `${currency ? `${currency} ` : ""}${number.toLocaleString("en-IN")}`;
  }
}

function getEmployeeField(employee, ...names) {
  for (const name of names) {
    if (employee?.[name] !== undefined && employee?.[name] !== null && String(displayValue(employee[name])).trim() !== "") {
      return employee[name];
    }
  }
  return "";
}

function getEmployeeText(employee, ...names) {
  return displayValue(getEmployeeField(employee, ...names));
}

function getPolicySearchText(employee) {
  return [
    getEmployeeText(employee, "leavePolicyId", "LeavePolicyId"),
    getEmployeeText(employee, "attendancePolicyId", "AttendancePolicyId"),
    getEmployeeText(employee, "shiftPatternId", "ShiftPatternId", "shiftPolicyId", "ShiftPolicyId"),
    getEmployeeText(employee, "weeklyOffPatternId", "WeeklyOffPatternId"),
    getEmployeeText(employee, "holidayCalendarId", "HolidayCalendarId"),
  ].filter(Boolean).join(" ");
}

function EmployeeMasterTable({ employees = [], loading = false, onRefresh, onEdit, onViewHistory }) {
  const [searchText, setSearchText] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");

  const departments = useMemo(
    () => [...new Set(employees.map((e) => getEmployeeText(e, "department", "Department", "departmentCode", "DepartmentCode")).filter(Boolean))].sort(),
    [employees]
  );
  const statuses = useMemo(
    () => [...new Set(employees.map((e) => getEmployeeText(e, "employmentStatus", "EmploymentStatus")).filter(Boolean))].sort(),
    [employees]
  );
  const countries = useMemo(
    () => [...new Set(employees.map((e) => getEmployeeText(e, "country", "Country", "countryCode", "CountryCode")).filter(Boolean))].sort(),
    [employees]
  );

  const filteredEmployees = useMemo(() => {
    const q = searchText.toLowerCase().trim();
    return employees.filter((employee) => {
      const searchableText = [
        getEmployeeText(employee, "employeeId", "EmpId", "EmployeeId", "EmployeeCode"),
        getEmployeeText(employee, "workEmail", "WorkEmail"),
        getEmployeeText(employee, "fullName", "FullName", "firstName", "FirstName", "lastName", "LastName"),
        getEmployeeText(employee, "country", "Country"),
        getEmployeeText(employee, "countryCode", "CountryCode"),
        getEmployeeText(employee, "legalEntityCode", "LegalEntityCode"),
        getEmployeeText(employee, "legalEntity", "LegalEntity"),
        getEmployeeText(employee, "location", "Location"),
        getEmployeeText(employee, "locationCode", "LocationCode", "workLocationId", "WorkLocationId"),
        getEmployeeText(employee, "regionState", "RegionState"),
        getEmployeeText(employee, "department", "Department"),
        getEmployeeText(employee, "departmentCode", "DepartmentCode"),
        getEmployeeText(employee, "workUnitCode", "WorkUnitCode", "productionUnitCode", "ProductionUnitCode"),
        getEmployeeText(employee, "designation", "Designation"),
        getEmployeeText(employee, "reportingManager", "ReportingManager"),
        getEmployeeText(employee, "managerEmployeeCode", "ManagerEmployeeCode"),
        getEmployeeText(employee, "employmentType", "EmploymentType"),
        getEmployeeText(employee, "workArrangement", "WorkArrangement"),
        getEmployeeText(employee, "payGrade", "PayGrade"),
        getEmployeeText(employee, "salaryCurrency", "SalaryCurrency"),
        getPolicySearchText(employee),
        getEmployeeText(employee, "currentEmployeeCTCId", "CurrentEmployeeCTCId"),
        getEmployeeText(employee, "currentCTCStructureId", "CurrentCTCStructureId"),
        getEmployeeText(employee, "compensationStatus", "CompensationStatus", "currentCTCStatus", "CurrentCTCStatus"),
      ].join(" ").toLowerCase();

      const departmentValue = getEmployeeText(employee, "department", "Department", "departmentCode", "DepartmentCode");
      const statusValue = getEmployeeText(employee, "employmentStatus", "EmploymentStatus");
      const countryValue = getEmployeeText(employee, "country", "Country", "countryCode", "CountryCode");

      return (
        (!q || searchableText.includes(q)) &&
        (!departmentFilter || departmentValue === departmentFilter) &&
        (!statusFilter || statusValue === statusFilter) &&
        (!countryFilter || countryValue === countryFilter)
      );
    });
  }, [employees, searchText, departmentFilter, statusFilter, countryFilter]);

  return (
    <div style={cardStyle}>
      <div style={headerRowStyle}>
        <div>
          <h2 style={sectionTitleStyle}>Employee Records</h2>
          <p style={smallTextStyle}>Showing {filteredEmployees.length} of {employees.length} employees</p>
        </div>
        <button type="button" onClick={onRefresh} style={refreshButtonStyle} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div style={filterGridStyle}>
        <input
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder="Search employee, email, country, legal entity, location, work unit, policy ID, PayGrade, CTC ID..."
          style={inputStyle}
        />
        <select value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)} style={inputStyle}>
          <option value="">All Departments</option>
          {departments.map((department) => <option key={department} value={department}>{department}</option>)}
        </select>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} style={inputStyle}>
          <option value="">All Statuses</option>
          {statuses.map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
        <select value={countryFilter} onChange={(event) => setCountryFilter(event.target.value)} style={inputStyle}>
          <option value="">All Countries</option>
          {countries.map((country) => <option key={country} value={country}>{country}</option>)}
        </select>
      </div>

      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Employee</th>
              <th style={thStyle}>Country / Legal Entity</th>
              <th style={thStyle}>Location / Region</th>
              <th style={thStyle}>Department / Work Unit</th>
              <th style={thStyle}>Employment</th>
              <th style={thStyle}>Policy IDs</th>
              <th style={thStyle}>Pay Grade</th>
              <th style={thStyle}>CTC Summary</th>
              <th style={thStyle}>Profile / Onboarding</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.length === 0 && (
              <tr>
                <td style={emptyCellStyle} colSpan="10">{loading ? "Loading employee records..." : "No employees found."}</td>
              </tr>
            )}

            {filteredEmployees.map((employee) => {
              const employeeId = getEmployeeText(employee, "employeeId", "EmpId", "EmployeeId", "EmployeeCode");
              const workEmail = getEmployeeText(employee, "workEmail", "WorkEmail");
              const country = getEmployeeText(employee, "country", "Country");
              const countryCode = getEmployeeText(employee, "countryCode", "CountryCode");
              const legalEntityCode = getEmployeeText(employee, "legalEntityCode", "LegalEntityCode");
              const legalEntity = getEmployeeText(employee, "legalEntity", "LegalEntity");
              const locationCode = getEmployeeText(employee, "locationCode", "LocationCode", "workLocationId", "WorkLocationId");
              const location = getEmployeeText(employee, "location", "Location");
              const regionState = getEmployeeText(employee, "regionState", "RegionState");
              const department = getEmployeeText(employee, "department", "Department");
              const departmentCode = getEmployeeText(employee, "departmentCode", "DepartmentCode");
              const workUnitCode = getEmployeeText(employee, "workUnitCode", "WorkUnitCode", "productionUnitCode", "ProductionUnitCode");
              const designation = getEmployeeText(employee, "designation", "Designation");
              const reportingManager = getEmployeeText(employee, "reportingManager", "ReportingManager");
              const managerEmployeeCode = getEmployeeText(employee, "managerEmployeeCode", "ManagerEmployeeCode");
              const employmentStatus = getEmployeeText(employee, "employmentStatus", "EmploymentStatus");
              const employmentType = getEmployeeText(employee, "employmentType", "EmploymentType");
              const workArrangement = getEmployeeText(employee, "workArrangement", "WorkArrangement");
              const ftePercent = getEmployeeText(employee, "ftePercent", "FTEPercent");
              const joiningDate = getEmployeeText(employee, "joiningDate", "JoiningDate");
              const timeZone = getEmployeeText(employee, "timeZone", "TimeZone");
              const leavePolicyId = getEmployeeText(employee, "leavePolicyId", "LeavePolicyId");
              const attendancePolicyId = getEmployeeText(employee, "attendancePolicyId", "AttendancePolicyId");
              const shiftPatternId = getEmployeeText(employee, "shiftPatternId", "ShiftPatternId", "shiftPolicyId", "ShiftPolicyId");
              const weeklyOffPatternId = getEmployeeText(employee, "weeklyOffPatternId", "WeeklyOffPatternId");
              const holidayCalendarId = getEmployeeText(employee, "holidayCalendarId", "HolidayCalendarId");
              const payGrade = getEmployeeText(employee, "payGrade", "PayGrade");
              const currency = getEmployeeText(employee, "salaryCurrency", "SalaryCurrency") || "INR";
              const compensationStatus = getEmployeeText(employee, "compensationStatus", "CompensationStatus", "currentCTCStatus", "CurrentCTCStatus");
              const currentCTCId = getEmployeeText(employee, "currentEmployeeCTCId", "CurrentEmployeeCTCId");
              const currentCTCStructureId = getEmployeeText(employee, "currentCTCStructureId", "CurrentCTCStructureId");
              const annualCTC = getEmployeeField(employee, "annualCTC", "AnnualCTC");
              const monthlyGross = getEmployeeField(employee, "monthlyGross", "MonthlyGross");
              const revisionDueFlag = getEmployeeField(employee, "revisionDueFlag", "RevisionDueFlag") === true || getEmployeeField(employee, "revisionDueFlag", "RevisionDueFlag") === "true" || getEmployeeField(employee, "revisionDueFlag", "RevisionDueFlag") === "Yes";
              const profileCompletionStatus = getEmployeeText(employee, "profileCompletionStatus", "ProfileCompletionStatus");
              const profileVerificationStatus = getEmployeeText(employee, "profileVerificationStatus", "ProfileVerificationStatus");
              const onboardingStage = getEmployeeText(employee, "onboardingStage", "OnboardingStage");

              return (
                <tr key={employee.id || employee.ID || employeeId || workEmail}>
                  <td style={tdStyle}>
                    <strong>{employeeId || "-"}</strong>
                    <div style={mutedTextStyle}>{workEmail || "-"}</div>
                    <div style={mutedTextStyle}>{designation || "-"}</div>
                  </td>

                  <td style={tdStyle}>
                    <strong>{country || "-"}</strong>
                    <div style={mutedTextStyle}>Country Code: {countryCode || "-"}</div>
                    <div style={mutedTextStyle}>Legal Entity: {legalEntityCode || legalEntity || "-"}</div>
                    {legalEntity && legalEntityCode && <div style={mutedTextStyle}>{legalEntity}</div>}
                  </td>

                  <td style={tdStyle}>
                    <strong>{locationCode || location || "-"}</strong>
                    {location && locationCode && <div style={mutedTextStyle}>{location}</div>}
                    <div style={mutedTextStyle}>Region: {regionState || "-"}</div>
                    <div style={mutedTextStyle}>Time Zone: {timeZone || "-"}</div>
                  </td>

                  <td style={tdStyle}>
                    <strong>{departmentCode || department || "-"}</strong>
                    {department && departmentCode && <div style={mutedTextStyle}>{department}</div>}
                    <div style={mutedTextStyle}>Work Unit: {workUnitCode || "-"}</div>
                    <div style={mutedTextStyle}>Manager: {managerEmployeeCode || reportingManager || "-"}</div>
                  </td>

                  <td style={tdStyle}>
                    <strong>{employmentStatus || "-"}</strong>
                    <div style={mutedTextStyle}>{employmentType || "-"} / {workArrangement || "-"}</div>
                    <div style={mutedTextStyle}>FTE {ftePercent || "-"}</div>
                    <div style={mutedTextStyle}>Joining: {joiningDate || "-"}</div>
                  </td>

                  <td style={tdStyle}>
                    <div><b>Leave:</b> {leavePolicyId || "-"}</div>
                    <div style={mutedTextStyle}><b>Attendance:</b> {attendancePolicyId || "-"}</div>
                    <div style={mutedTextStyle}><b>Shift:</b> {shiftPatternId || "-"}</div>
                    <div style={mutedTextStyle}><b>Weekly Off:</b> {weeklyOffPatternId || "-"}</div>
                    <div style={mutedTextStyle}><b>Holiday:</b> {holidayCalendarId || "-"}</div>
                  </td>

                  <td style={tdStyle}>
                    <strong>{payGrade || "-"}</strong>
                    <div style={mutedTextStyle}>Currency: {currency || "-"}</div>
                    <div style={mutedTextStyle}>Comp: {compensationStatus || "-"}</div>
                  </td>

                  <td style={tdStyle}>
                    <strong>{currentCTCId || "No CTC"}</strong>
                    <div style={mutedTextStyle}>Structure: {currentCTCStructureId || "-"}</div>
                    <div style={mutedTextStyle}>Annual: {formatMoney(annualCTC, currency)}</div>
                    <div style={mutedTextStyle}>Monthly Gross: {formatMoney(monthlyGross, currency)}</div>
                    {revisionDueFlag && <div style={revisionPillStyle}>Revision Due</div>}
                  </td>

                  <td style={tdStyle}>
                    <strong>{onboardingStage || "-"}</strong>
                    <div style={mutedTextStyle}>Profile: {profileCompletionStatus || "-"}</div>
                    <div style={mutedTextStyle}>Verify: {profileVerificationStatus || "-"}</div>
                  </td>

                  <td style={tdStyle}>
                    <div style={buttonStackStyle}>
                      <button type="button" onClick={() => onEdit(employee)} style={editButtonStyle}>Edit</button>
                      <button type="button" onClick={() => onViewHistory?.(employee)} style={historyButtonStyle}>History</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const cardStyle = { backgroundColor: "var(--fm-card)", border: "1px solid var(--fm-border)", borderRadius: "14px", padding: "20px", marginBottom: "18px", boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)" };
const headerRowStyle = { display: "flex", justifyContent: "space-between", gap: "12px", alignItems: "center", marginBottom: "14px" };
const sectionTitleStyle = { marginTop: 0, marginBottom: "4px", fontSize: "20px" };
const smallTextStyle = { margin: 0, color: "var(--fm-muted)", fontSize: "13px" };
const filterGridStyle = { display: "grid", gridTemplateColumns: "2fr repeat(3, 1fr)", gap: "12px", marginBottom: "16px" };
const inputStyle = { height: "38px", border: "1px solid var(--fm-border)", borderRadius: "8px", padding: "8px 10px", fontSize: "14px", backgroundColor: "var(--fm-card)", boxSizing: "border-box" };
const tableWrapperStyle = { overflowX: "auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "13px", minWidth: "1680px" };
const thStyle = { textAlign: "left", borderBottom: "1px solid var(--fm-border)", padding: "10px", backgroundColor: "#f8fafc", color: "var(--fm-text)", whiteSpace: "nowrap" };
const tdStyle = { borderBottom: "1px solid var(--fm-border)", padding: "10px", color: "var(--fm-text)", whiteSpace: "nowrap", verticalAlign: "top" };
const mutedTextStyle = { color: "var(--fm-muted)", fontSize: "12px", marginTop: "4px" };
const emptyCellStyle = { padding: "18px", textAlign: "center", color: "var(--fm-muted)" };
const refreshButtonStyle = { backgroundColor: "var(--fm-card)", color: "var(--fm-text)", border: "1px solid var(--fm-border)", borderRadius: "8px", padding: "9px 14px", fontWeight: 700, cursor: "pointer" };
const editButtonStyle = { backgroundColor: "#0f766e", color: "#ffffff", border: "none", borderRadius: "7px", padding: "7px 12px", fontWeight: 700, cursor: "pointer" };
const historyButtonStyle = { backgroundColor: "#1d4ed8", color: "#ffffff", border: "none", borderRadius: "7px", padding: "7px 12px", fontWeight: 700, cursor: "pointer" };
const buttonStackStyle = { display: "flex", gap: "8px" };
const revisionPillStyle = { display: "inline-block", marginTop: "6px", background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a", borderRadius: "999px", padding: "2px 8px", fontSize: "11px", fontWeight: 800 };

export default EmployeeMasterTable;
